import moment, { Moment } from "moment";

import { buildDefaultExposureName, slugifyExposureName } from "../../types/api/istio.type";
import {
  IClonePath,
  ICloneSource,
  ICloneTarget,
  IForeignSource,
  IImageCheck,
  IRebuildVerify,
  IRestoreWindow,
  ISourceSpec,
} from "../../types/api/restore.type";

const UNITS: Record<string, number> = {
  Ki: 2 ** 10, Mi: 2 ** 20, Gi: 2 ** 30, Ti: 2 ** 40, Pi: 2 ** 50, Ei: 2 ** 60,
  k: 1e3, M: 1e6, G: 1e9, T: 1e12, P: 1e15, E: 1e18, "": 1,
};

/** A Kubernetes storage quantity in bytes, or null if it is not one; the same rule as database-manager's. */
export const toBytes = (quantity?: string | null): number | null => {
  const m = /^\s*([0-9]+(?:\.[0-9]+)?)([KMGTPE]i|[kMGTPE])?\s*$/.exec(quantity ?? "");
  return m ? Number(m[1]) * UNITS[m[2] ?? ""] : null;
};

/** D10: the source's data must fit. An unknown size on either side sets no floor. */
export const belowFloor = (storage?: string | null, floor?: string | null): boolean => {
  const wanted = toBytes(storage);
  const minimum = toBytes(floor);
  return wanted !== null && minimum !== null && wanted < minimum;
};

/** Bytes as the whole Gi that holds them, as database-manager states another cluster's floor. */
export const gibibytes = (bytes: number) => `${Math.ceil(bytes / 2 ** 30)}Gi`;

/** D10: the recorded spec's storage or, for another cluster's source, its database size (D17). */
export const floorOf = (window?: IRestoreWindow | null): string | undefined =>
  window?.source_spec?.storage ?? (window?.source_size ? gibibytes(window.source_size) : undefined);

/** Above another cluster's database size, for the WAL its restore replays and the clone's growth. */
export const HEADROOM = 1.25;

export type Source = ICloneSource | IForeignSource;

export const isForeign = (source: Source): source is IForeignSource => "cluster" in source;

/** Ids repeat across clusters (dev and prod both have a 10185), and a folder may be in two accounts. */
export const sourceKey = (source: Source) =>
  isForeign(source) ? `${source.cluster}/${source.account}/${source.env_id}` : String(source.env_id);

/** Whether `window` was read for `source`, not for an environment with the same id elsewhere. */
export const windowIsFor = (window: IRestoreWindow | null | undefined, source: Source | null): boolean => {
  if (!window || !source || window.env_id !== source.env_id) return false;
  if (!isForeign(source)) return !window.cluster;
  return window.cluster === source.cluster && window.repository?.split("/")[0] === source.account;
};

/** D19: another cluster's sources are found by id; its folder name works too. */
export const foreignEnvId = (query: string): number | undefined => {
  const m = /^\s*(?:b[ae]tch-env-)?(\d+)\s*$/.exec(query);
  return m ? Number(m[1]) : undefined;
};

export const windowBounds = (window: IRestoreWindow) => ({
  earliest: moment.utc(window.earliest),
  latest: moment.utc(window.latest),
});

/** Compared at the API's own precision (microseconds), not Moment's milliseconds. */
export const withinWindow = (point: string, window: IRestoreWindow): boolean => {
  const at = toMicros(point);
  return at !== null && at >= toMicros(window.earliest)! && at <= toMicros(window.latest)!;
};

const toMicros = (iso: string): number | null => {
  const m = /^(.*T\d\d:\d\d:\d\d)(?:\.(\d{1,6}))?(Z|[+-]\d\d:?\d\d)?$/.exec(iso.trim());
  if (!m) return null;
  const seconds = moment.utc(`${m[1]}${m[3] ?? "Z"}`);
  return seconds.isValid() ? seconds.valueOf() * 1000 + Number((m[2] ?? "").padEnd(6, "0")) : null;
};

export interface BackupEntry {
  label: string;
  /** The set's end as the API sent it, so a pick keeps its precision. */
  value: string;
  kind: "full" | "incremental" | "differential";
}

export interface BackupGroup {
  /** The full backup the entries build on. */
  full: string;
  entries: BackupEntry[];
}

const KIND: Record<string, BackupEntry["kind"]> = { F: "full", I: "incremental", D: "differential" };

/** D12: backup ends to jump to, grouped by full backup, newest first; the latest end is a button of its own. */
export const backupGroups = (window: IRestoreWindow): BackupGroup[] => {
  const groups = new Map<string, BackupEntry[]>();
  [...window.backup_sets]
    .filter((set) => set.stop && withinWindow(set.stop, window))
    .sort((a, b) => (toMicros(b.stop!)! - toMicros(a.stop!)!))
    .forEach((set) => {
      const full = set.label.split("_")[0];
      const entry = { label: set.label, value: set.stop!, kind: KIND[set.label.slice(-1)] ?? "full" };
      groups.set(full, [...(groups.get(full) ?? []), entry]);
    });
  return Array.from(groups, ([full, entries]) => ({ full, entries }));
};

export const utcLabel = (iso: string) => `${moment.utc(iso).format("YYYY-MM-DD HH:mm:ss")} UTC`;
export const localLabel = (iso: string) => moment(iso).local().format("YYYY-MM-DD HH:mm:ss Z");

/** D11: answers that create nothing. Anything else (a timeout, a 5xx) may have created the database. */
const REFUSALS = [400, 403, 404, 409, 422, 429, 503];
export const isRefusal = (status?: number) => status !== undefined && REFUSALS.includes(status);

/** What a clone call's answer means; `undefined` is no answer at all. */
export const cloneOutcome = (status?: number): "created" | "refused" | "uncertain" =>
  status !== undefined && status >= 200 && status < 300 ? "created" : isRefusal(status) ? "refused" : "uncertain";

/** Each call starts a request and returns its check: true until a newer request starts. */
export const latestOnly = () => {
  let latest = 0;
  return () => {
    const mine = ++latest;
    return () => mine === latest;
  };
};

/**
 * A fetch stream where only the latest selection counts. Each call supersedes the last and sets `pending`
 * at once: false for a cleared selection (null), true otherwise. Only the latest request may report back.
 */
export const latestStream = <K, T>(
  fetch: (key: K) => Promise<T>,
  on: { data: (data: T) => void; error: (e: unknown) => void; pending: (pending: boolean) => void },
  delayMs = 0
) => {
  const guard = latestOnly();
  let timer: ReturnType<typeof setTimeout> | undefined;
  return (key: K | null) => {
    const isCurrent = guard();
    clearTimeout(timer);
    on.pending(key !== null);
    if (key === null) return;
    const selected = key;
    timer = setTimeout(() => {
      fetch(selected)
        .then((data) => isCurrent() && on.data(data), (e) => isCurrent() && on.error(e))
        .finally(() => isCurrent() && on.pending(false));
    }, delayMs);
  };
};

/** The operator's sentence for a failed clone, from the server's detail. */
export const refusalMessage = (status: number | undefined, detail: any): string => {
  const said = detail?.message || "The clone failed";
  if (detail?.minimum_storage) return `${said}. Storage must be at least ${detail.minimum_storage}.`;
  if (detail?.earliest && detail?.latest) {
    return `${said}: ${utcLabel(detail.earliest)} to ${utcLabel(detail.latest)}.`;
  }
  if (status === 429 && !/try again/i.test(said)) return `${said}. Try again once a running restore finishes.`;
  // A 503 is also "no external port"; only a bare one is taken to mean cloning is off.
  if (status === 503 && !detail?.message) return "Cloning is switched off on this server (ENVIRONMENT_CLONE_ENABLED).";
  return said;
};

export interface CloneForm extends ISourceSpec {
  name: string;
  password: string;
  /** D15: off by default; a clone is a new database and may be exposed like one. */
  expose_externally: boolean;
  exposure_name: string;
}

/** The form's defaults: the source's spec (D10), with no name or password yet. */
export const formFromSpec = (spec?: ISourceSpec | null): CloneForm => ({
  name: "",
  password: "",
  expose_externally: false,
  exposure_name: "",
  storage: spec?.storage ?? "",
  cpu_req: spec?.cpu_req ?? "1",
  memory_req: spec?.memory_req ?? "2Gi",
  cpus: spec?.cpus ?? null,
  memory: spec?.memory ?? null,
  cpu_bkp: spec?.cpu_bkp ?? "250m",
  memory_bkp: spec?.memory_bkp ?? "256Mi",
  tier: spec?.tier ?? "shared",
  storage_class: spec?.storage_class ?? "cr-basic-retain",
  nodepool: spec?.nodepool ?? "database",
  resource_allocation: spec?.resource_allocation ?? "request_only",
  // D23: off on every clone, whatever the source had: its rules come with the data, real destinations included.
  rule_engine: false,
  full_backup_schedule: spec?.full_backup_schedule ?? "weekly",
  incremental_backup_schedule: spec?.incremental_backup_schedule ?? "daily",
  backup_storage_account: spec?.backup_storage_account ?? "",
});

/** The window's source spec or, for another cluster's source, the defaults with room for its database. */
export const formFromWindow = (window: IRestoreWindow): CloneForm => {
  const form = formFromSpec(window.source_spec);
  return form.storage || !window.source_size ? form : { ...form, storage: gibibytes(window.source_size * HEADROOM) };
};

/** The accounts row, as the create form sends it. Never made primary here: database-manager does that once the
 *  clone exists, and only if the tenant has none (D22). */
export const accountsBody = (form: CloneForm, tenantId: number, owner: number) => ({
  description: form.name.trim(),
  is_batch: false,
  is_subscription: false,
  is_trial: false,
  make_prod: false,
  owner,
  status: "starting",
  tenant_id: tenantId,
});

/** The exposure name to ask for: the operator's, slugified, or the server's default for this tenant and name. */
export const exposureNameFor = (form: CloneForm, tenantName?: string | null) =>
  form.exposure_name.trim() ? slugifyExposureName(form.exposure_name) : buildDefaultExposureName(tenantName, form.name.trim());

/** D16: everything the source has written when the clone is taken, its last write included. */
export const LATEST = "latest";

/** What Latest holds, for the hint and the confirm dialog: another cluster's source gives only what it has archived. */
export const latestMeaning = (window?: IRestoreWindow | null) =>
  window?.cluster
    ? `everything in ${window.cluster}'s WAL archive when the clone starts; writes ${window.cluster} has not archived by then are not in it`
    : "everything the source has written when the clone starts, its last write included";

export interface CloneOptions {
  /** Sent only when exposing. */
  exposureName?: string;
  /** Answers "no port left" (D15). */
  proceedWithoutExposure?: boolean;
  /** The target tenant's name as typed, for a clone across accounts (D24). */
  confirmTenantName?: string;
  /** A cr-timescaledb tag to run once restored (D25); blank or the default sends nothing. */
  image?: string;
  defaultImage?: string | null;
}

/** The clone request. `point` is a time, or LATEST. A blank password is left out, so the server generates one. */
export const cloneBody = (
  form: CloneForm,
  target: { env_id: number; env_uuid: string },
  point: string,
  { exposureName, proceedWithoutExposure = false, confirmTenantName, image, defaultImage }: CloneOptions = {}
) => {
  const { name, password, exposure_name, ...spec } = form;
  return {
    environment: {
      ...spec,
      ...(form.expose_externally ? { exposure_name: exposureName, proceed_without_exposure: proceedWithoutExposure } : {}),
      // Blank is "no limit"; the server rejects an empty quantity.
      cpus: spec.cpus || null,
      memory: spec.memory || null,
      id: target.env_id,
      uuid: target.env_uuid,
      ...(password ? { password } : {}),
    },
    ...(point === LATEST ? { latest: true } : { point }),
    ...(confirmTenantName ? { confirm_tenant_name: confirmTenantName.trim() } : {}),
    ...(runtimeImage(image, defaultImage) ? { image: image!.trim() } : {}),
  };
};

/** D25: the image to ask for as typed (a tag or repository:tag; the server checks the repository), or "" for
 *  the cluster's default. */
export const runtimeImage = (image?: string | null, defaultImage?: string | null) => {
  const typed = (image ?? "").trim();
  return typed && tagOf(typed) !== (defaultImage ?? "").trim() ? typed : "";
};

/** D25: the Restore tab's line for a clone given a runtime image. Compared by tag, and a refusal said outright. */
export const runtimeLine = (runtime?: IRebuildVerify["runtime"]) => {
  if (!runtime?.requested) return "";
  const running = tagOf(runtime.running ?? ""), requested = tagOf(runtime.requested);
  const schema = runtime.schema_version ? ` · schema ${runtime.schema_version}` : "";
  if (running === requested) return `Runtime image: ${running}${schema}`;
  if (runtime.refused) return `Runtime image ${requested} refused: ${runtime.refused}`;
  return `Runtime image: ${running}, waiting to switch to ${requested}${schema}`;
};

/** D25: Clone waits for a passed check, unless the clone runs the default. */
export const imageReady = (image: string, defaultImage: string | null | undefined, check?: IImageCheck | null) =>
  !runtimeImage(image, defaultImage) || (check?.tag === tagOf(runtimeImage(image, defaultImage)) && check.state === "ok");

/** The tag of `repository:tag`, or the tag itself; the server answers checks by tag. */
export const tagOf = (image: string) => image.trim().split(":").pop() ?? "";

/** D25: the check's state as one line. */
export const imageCheckText = (check?: IImageCheck | null) =>
  !check || check.state === "checking" || check.state == null
    ? "Checking the image: pulling it and reading its migrations…"
    : check.state === "ok"
    ? `${check.migrations} migrations, the last ${check.last_migration}. It takes over once the restore has finished.`
    : check.detail || "The image could not be checked.";

/** D25 presets: the cluster's default, and the image a running source on this cluster runs now. */
export const imagePresets = (window?: IRestoreWindow | null) =>
  [
    window?.default_image ? { tag: window.default_image, label: "This cluster's default" } : null,
    window?.source_image && window.source_image !== window.default_image
      ? { tag: window.source_image, label: "The image the source runs now" }
      : null,
  ].filter((p): p is { tag: string; label: string } => p !== null);

/** "FXI › FXI Ops › Tupelo › FXI Tupelo": where a tenant sits (D21). */
export const pathLabel = (path?: IClonePath | null) =>
  [path?.account_name, path?.organization_name, path?.location_name, path?.tenant_name].filter(Boolean).join(" › ");

/** D24: another cluster's source, or an account unknown on either side, counts as another account. */
export const crossesAccounts = (source: Source, target: IClonePath) =>
  isForeign(source) || source.account_id == null || target.account_id == null || source.account_id !== target.account_id;

/** D24: the typed name must be the target tenant's, as database-manager compares it; typing nothing never is. */
export const confirmsTenant = (typed: string, target?: { tenant_name?: string | null } | null) =>
  !!typed.trim() && typed.trim() === (target?.tenant_name ?? "").trim();

/** D22: what the clone does to the target tenant's primary database. */
export const primaryNote = (target: ICloneTarget) =>
  target.primary_env_id == null
    ? `It becomes ${target.tenant_name}'s primary database: users who open ${target.tenant_name} are taken to it.`
    : `It is added beside ${target.tenant_name}'s primary, ${target.primary_env_name || "(no name)"} ` +
      `(${target.primary_env_id}), which stays primary.`;

/** D23: shown when the rule engine is switched on. */
export const ruleEngineWarning = (sourceName: string) =>
  `This clone holds ${sourceName}'s alert rules with their real destinations (email addresses, Teams channels). ` +
  `With a rule engine on, its alerts can reach ${sourceName}'s users.`;

/** D17: the same request, with the source named by cluster, account and id. */
export const foreignCloneBody = (source: IForeignSource, request: ReturnType<typeof cloneBody>) => ({
  ...request,
  cluster: source.cluster,
  account: source.account,
  source_env_id: source.env_id,
});

/** A picked time as the API's ISO string (UTC). */
export const pointFrom = (picked: Moment) => picked.clone().utc().toISOString();
